// Browser-mic eval recorder: raw PCM capture → 16k mono PCM16 WAV.
// MediaRecorder would hand us webm/opus, which the server would then have to
// decode; capturing PCM here and resampling with an OfflineAudioContext
// keeps the server WAV-only (same container the phone leg produces).

export interface EvalRecorder {
  stop: () => Promise<Blob>;
  cancel: () => void;
}

const TARGET_RATE = 16000;

export async function startEvalRecorder(): Promise<EvalRecorder> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
  });
  const ctx = new AudioContext();
  const source = ctx.createMediaStreamSource(stream);
  // ScriptProcessor is deprecated but universally supported and fine for a
  // manual admin tool; 4096-sample buffers ≈ 85ms at 48k.
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  const chunks: Float32Array[] = [];
  processor.onaudioprocess = e => {
    chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
  };
  source.connect(processor);
  processor.connect(ctx.destination);

  const teardown = () => {
    processor.disconnect();
    source.disconnect();
    stream.getTracks().forEach(t => t.stop());
    void ctx.close();
  };

  return {
    cancel: teardown,
    stop: async () => {
      teardown();
      const total = chunks.reduce((n, c) => n + c.length, 0);
      const joined = new Float32Array(total);
      let off = 0;
      for (const c of chunks) { joined.set(c, off); off += c.length; }

      // Resample to 16k via OfflineAudioContext rendering.
      const frames = Math.max(1, Math.ceil((total * TARGET_RATE) / ctx.sampleRate));
      const offline = new OfflineAudioContext(1, frames, TARGET_RATE);
      const buffer = offline.createBuffer(1, total, ctx.sampleRate);
      buffer.copyToChannel(joined, 0);
      const node = offline.createBufferSource();
      node.buffer = buffer;
      node.connect(offline.destination);
      node.start();
      const rendered = await offline.startRendering();
      const pcm = rendered.getChannelData(0);

      return encodeWav(pcm, TARGET_RATE);
    },
  };
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeString = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);          // PCM
  view.setUint16(22, 1, true);          // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++, off += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
}
